import { useEffect, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const SWIPE_THRESHOLD = 50;

export const BottomSheet = ({ isOpen, onClose, children }: BottomSheetProps) => {
  const [swipeStartY, setSwipeStartY] = useState<number | null>(null);
  const [currentTranslateY, setCurrentTranslateY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
      return undefined;
    }

    setIsAnimating(false);
    setCurrentTranslateY(0);

    const timer = setTimeout(() => setIsVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleTouchStart = (e: any) => {
    const touch = e.nativeEvent?.touches?.[0];
    if (touch) {
      setSwipeStartY(touch.pageY);
    }
  };

  const handleTouchMove = (e: any) => {
    if (swipeStartY === null) return;

    const touch = e.nativeEvent?.touches?.[0];
    if (!touch) return;

    const swipeDistance = touch.pageY - swipeStartY;

    // Only drag if swiping down
    if (swipeDistance > 0) {
      setCurrentTranslateY(swipeDistance);
      setIsDragging(true);
      // The live drag offset is a continuous runtime value, so it can't be a
      // static Tailwind class, and atoms primitives reject the `style` prop.
      // Feed it to the className through a CSS variable instead.
      sheetRef.current?.style.setProperty('--bottom-sheet-drag-y', `${swipeDistance}px`);
    }
  };

  const handleTouchEnd = () => {
    if (swipeStartY === null) return;

    setIsDragging(false);
    setSwipeStartY(null);

    if (currentTranslateY > SWIPE_THRESHOLD) {
      onClose();
    } else {
      setCurrentTranslateY(0);
    }
  };

  if (!isVisible) return null;

  return (
    <Box
      className={cn(
        'fixed inset-0 z-[1000] touch-none transition-colors duration-200 ease-out',
        isAnimating ? 'bg-black/70' : 'bg-black/0',
      )}
      onTouchStart={(e) => {
        e.preventDefault();
        onClose();
      }}
      onTouchMove={(e) => e.preventDefault()}
    >
      <Col
        ref={sheetRef}
        className={cn(
          'absolute inset-x-0 bottom-0',
          isDragging
            ? 'translate-y-[var(--bottom-sheet-drag-y)] transition-none'
            : cn(
                'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
                isAnimating ? 'translate-y-0' : 'translate-y-full',
              ),
        )}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Col
          className="w-full cursor-grab items-center pb-2 pt-1.5"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Box className="h-2 w-22 rounded-full bg-od-border-primary" />
        </Col>
        <Col
          className="w-full rounded-t-[2rem] bg-od-bg-primary"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {children}
        </Col>
      </Col>
    </Box>
  );
};
